import React, { useState, useMemo } from "react";
import toast from "react-hot-toast";
import { Eye, EyeOff } from "lucide-react";
import { useTranslation } from "react-i18next";

const SentenceBuilder = () => {
  const { t, i18n } = useTranslation();

  // ✅ Get sentences from i18n JSON
  const sentences = useMemo(() => {
    return t("sentenceBuilder.sentences", {
      returnObjects: true,
      defaultValue: [],
    });
  }, [i18n.language, t]);

  const shuffleWords = (sentence) => {
    if (!sentence) return [];
    return sentence.split(" ").sort(() => Math.random() - 0.5);
  };

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedWords, setSelectedWords] = useState([]);
  const [showAnswer, setShowAnswer] = useState(false);
  const [isWrong, setIsWrong] = useState(false);

  const correctSentence = sentences[currentIndex] || "";

  // ✅ Shuffle only when sentence changes
  const shuffledWords = useMemo(
    () => shuffleWords(correctSentence),
    [correctSentence]
  );

  const handleWordClick = (word) => {
    if (!selectedWords.includes(word)) {
      setSelectedWords((prev) => [...prev, word]);
    }
  };

  const handleReset = () => {
    setSelectedWords([]);
    setShowAnswer(false);
    setIsWrong(false);
  };

  const handleNext = () => {
    if (currentIndex < sentences.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      setSelectedWords([]);
      setShowAnswer(false);
      setIsWrong(false);
    } else {
      toast.success(t("sentenceBuilder.completedAll"));
    }
  };

  const handleCheck = () => {
    const userSentence = selectedWords.join(" ");
    if (userSentence === correctSentence) {
      toast.success(t("sentenceBuilder.shuffle.correct"));
      setIsWrong(false);
      setShowAnswer(false);
    } else {
      toast.error(t("sentenceBuilder.shuffle.incorrect"));
      setIsWrong(true);
      setShowAnswer(false);
    }
  };

  return (
    <div className="w-[90%] max-w-3xl mx-auto my-10 font-sans">
      <h2 className="text-4xl font-bold text-gray-800 text-center mb-2">
        {t("sentenceBuilder.title")}
      </h2>

      <p className="text-center text-gray-600 mb-4">
        {t("sentenceBuilder.subTitle")}
      </p>

      <p className="text-center text-gray-500 mb-4">
        {t("sentenceBuilder.sentenceCount", {
          current: currentIndex + 1,
          total: sentences.length,
        })}
      </p>

      {/* Selected Sentence Area */}
      <div className="min-h-[80px] border border-gray-300 rounded-lg p-4 mb-6 bg-white shadow-sm">
        {selectedWords.length === 0 ? (
          <p className="text-gray-400 text-center">
            {t("sentenceBuilder.emptySelected")}
          </p>
        ) : (
          <p className="text-lg font-medium">{selectedWords.join(" ")}</p>
        )}
      </div>

      {/* Shuffled Words */}
      <div className="flex flex-wrap gap-3 justify-center mb-8">
        {shuffledWords.map((word, idx) => (
          <button
            key={`${word}-${idx}`}
            onClick={() => handleWordClick(word)}
            disabled={selectedWords.includes(word)}
            className={`px-4 py-2 rounded-lg border text-gray-700 bg-gray-100 shadow-sm
              hover:bg-blue-100 hover:border-blue-500 transition-all
              ${
                selectedWords.includes(word)
                  ? "opacity-40 cursor-not-allowed"
                  : ""
              }`}
          >
            {word}
          </button>
        ))}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-center gap-5">
        <button
          onClick={handleReset}
          className="px-6 py-2 text-white bg-red-500 rounded-lg shadow hover:bg-red-600 transition"
        >
          {t("sentenceBuilder.buttons.reset")}
        </button>

        <button
          onClick={handleCheck}
          className="px-6 py-2 text-white bg-blue-600 rounded-lg shadow hover:bg-blue-700 transition"
        >
          {t("sentenceBuilder.buttons.check")}
        </button>

        <button
          onClick={handleNext}
          className="px-6 py-2 text-white bg-green-600 rounded-lg shadow hover:bg-green-700 transition"
        >
          {t("sentenceBuilder.buttons.next")}
        </button>
      </div>

      {/* Correct Sentence Toggle */}
      {isWrong && (
        <div className="mt-5 flex items-center justify-center gap-3 text-lg font-semibold text-gray-800">
          <p>{t("sentenceBuilder.showCorrect")}</p>

          <button
            onClick={() => setShowAnswer((prev) => !prev)}
            className="p-2 rounded-full hover:bg-gray-200 transition"
          >
            {showAnswer ? <EyeOff size={22} /> : <Eye size={22} />}
          </button>

          {showAnswer && (
            <span className="text-green-700">{correctSentence}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default SentenceBuilder;
